import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { defaultRouteFor, useAuth } from "../auth/AuthContext";
import { Button, Card, Input } from "../components/ui";
import antmileLogo from "../assets/antmile_logo.svg";

// LoginPage — standalone (no AppShell). Form clásico: email + password,
// loader durante validate, error inline si falla. Tras éxito redirige a
// la URL ?next= (si la sesión expiró mientras el user estaba en algún
// lado) o al home por rol.
export function LoginPage() {
  const { currentUser, login, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [bannerMsg, setBannerMsg] = useState<string | null>(null);

  // Decode ?msg= banners (drivers landing back here, session expired, etc.)
  useEffect(() => {
    const m = params.get("msg");
    if (m === "use-mobile-app") {
      setBannerMsg(
        "Los choferes operan desde la app móvil. La web es para admin, coordinadores y supervisores.",
      );
    } else if (m === "session-expired") {
      setBannerMsg("Tu sesión expiró. Inicia sesión de nuevo.");
    }
  }, [params]);

  // If already logged in, skip the form: jump to the right home (or to
  // ?next if the protected route bounced them here).
  useEffect(() => {
    if (!currentUser) return;
    const next = params.get("next");
    const target = next ? decodeURIComponent(next) : defaultRouteFor(currentUser.role);
    navigate(target, { replace: true });
  }, [currentUser, navigate, params]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email || !password) {
      setError("Ingresa email y contraseña.");
      return;
    }
    const errMsg = await login(email, password);
    if (errMsg) setError(errMsg);
    // Success path: the useEffect above redirects when currentUser flips.
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-page p-4">
      <div className="w-full max-w-sm">
        {/* Brand mark — mismo logo + tipografía que el sidebar para que
            el login se sienta parte de la misma app. */}
        <div className="text-center mb-6">
          <img
            src={antmileLogo}
            alt="antMile"
            className="w-16 h-16 mx-auto rounded-xl shadow-sm"
          />
          <div className="text-3xl font-bold text-sidebar mt-3 tracking-tight">
            ant<span className="text-secondary">Mile</span>
          </div>
          <p className="text-[11px] uppercase tracking-wider text-sub mt-1">
            Logística PYME
          </p>
        </div>

        <Card padding="lg">
          <h1 className="text-base font-semibold text-ink mb-1">
            Iniciar sesión
          </h1>
          <p className="text-xs text-sub mb-4">
            Accede con tu cuenta corporativa.
          </p>

          {bannerMsg && (
            <div className="mb-3 px-3 py-2 rounded-md bg-state-delayed/10 border border-state-delayed/30 text-state-delayed text-xs">
              {bannerMsg}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@empresa.com"
            />
            <Input
              label="Contraseña"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            {error && (
              <div className="px-3 py-2 rounded-md bg-state-failed/10 border border-state-failed/30 text-state-failed text-xs">
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={loading}
            >
              {loading ? "Verificando…" : "Iniciar sesión"}
            </Button>
          </form>

          {import.meta.env.DEV && (
            <div className="mt-5 pt-4 border-t border-border text-[11px] text-sub leading-relaxed">
              <div className="font-medium text-ink mb-1">Cuentas demo</div>
              <div>
                <span className="font-mono">admin@acme.local</span> /{" "}
                <span className="font-mono">coord@acme.local</span> /{" "}
                <span className="font-mono">super@acme.local</span>
              </div>
              <div className="mt-1">
                Contraseña: <span className="font-mono">password</span>
              </div>
            </div>
          )}
        </Card>

        <p className="text-center text-[11px] text-sub mt-4">
          ¿Problemas para entrar? Contacta a soporte interno.
        </p>
        <p className="text-center text-[10px] text-sub/70 font-mono mt-1">
          v{__APP_VERSION__}
        </p>
      </div>
    </div>
  );
}
